import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  Alert,
  CircularProgress,
  Grid,
  Divider,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useTheme } from '../../../../context/PermissionsContext/ThemeContext/ThemeContext';
import { GamifyIcon } from '../../../../components/GamifyIcon';
import { useCategories } from '../../../../hooks/useCategories';
import {
  DocuSealTemplate,
  Viewer,
  TemplateViewers,
  User,
  Team,
  CRM,
  fetchDocuSealTemplates,
  fetchUsersAndTeams,
  saveDocuSealViewers,
  initializeTemplateViewers,
  convertFromOrgFormat,
} from './docusealHelpers';
import AddViewerDialog from './AddViewerDialog';

const CustomCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  borderRadius: 12,
  boxShadow: theme.custom?.isDark
    ? '0px 2px 8px rgba(0, 0, 0, 0.3)'
    : '0px 2px 8px rgba(0, 0, 0, 0.1)',
  border: `1px solid ${theme.custom?.stroke || theme.palette.divider}`,
  backgroundColor: theme.custom?.background1,
}));

const DocuSealVisibility: React.FC<{ orgData: any }> = ({ orgData }) => {
  const [templates, setTemplates] = useState<DocuSealTemplate[]>([]);
  const [templateViewers, setTemplateViewers] = useState<TemplateViewers[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [addViewerDialog, setAddViewerDialog] = useState<{
    open: boolean;
    templateId: string;
  }>({ open: false, templateId: '' });
  const [availableUsers, setAvailableUsers] = useState<User[]>([]);
  const [availableTeams, setAvailableTeams] = useState<Team[]>([]);

  const { theme } = useTheme();
  const { categories: stageCategories } = useCategories();

  // Convert stage categories to CRM format for DocuSeal
  const availableCRMs: CRM[] = stageCategories
    .filter((cat): cat is typeof cat & { id: string; title: string } =>
      Boolean(cat.id && cat.title)
    )
    .map((cat) => ({
      id: cat.id,
      title: cat.title,
    }));

  // Load DocuSeal templates
  const loadDocuSealTemplates = async () => {
    try {
      // @ts-ignore
      const orgID = (global as any)?.me?.orgID;

      const templates = await fetchDocuSealTemplates(orgID);
      setTemplates(templates);

      if (templates.length === 0) {
        setError(
          'No DocuSeal templates found. Make sure you have created templates in your DocuSeal account.'
        );
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    }
  };

  // Initialize viewers for templates with existing data
  const handleInitializeViewers = async () => {
    try {
      let existingAccess: Record<string, string[]> | null = null;

      if (orgData?.docuSealAccess) {
        try {
          const parsed = JSON.parse(orgData.docuSealAccess);
          if (parsed && typeof parsed === 'object') {
            existingAccess = parsed;
          } else {
            console.warn('Invalid docuSealAccess format, using defaults');
          }
        } catch (parseError) {
          console.error(
            'Error parsing docuSealAccess from orgData:',
            parseError
          );
          setError(
            'Warning: Could not load existing DocuSeal settings. Using defaults.'
          );
        }
      }

      const templateViewersWithExistingData = await convertFromOrgFormat(
        templates,
        existingAccess,
        availableUsers,
        availableTeams
      );

      setTemplateViewers(templateViewersWithExistingData);
    } catch (error) {
      console.error('Error loading existing DocuSeal access:', error);
      setError('Warning: Could not load existing settings. Using defaults.');

      const defaultViewers = initializeTemplateViewers(templates);
      setTemplateViewers(defaultViewers);
    }
  };

  // Load users and teams for viewer selection
  const loadUsersAndTeams = async () => {
    try {
      // @ts-ignore
      const orgID = (global as any)?.me?.orgID;

      const { users, teams } = await fetchUsersAndTeams(orgID);
      setAvailableUsers(users);
      setAvailableTeams(teams);
    } catch (err) {
      console.error('Failed to fetch users and teams:', err);
      setAvailableUsers([]);
      setAvailableTeams([]);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await loadDocuSealTemplates();
      await loadUsersAndTeams();
      setLoading(false);
    };

    loadData();
  }, []); // Remove orgData dependency since we use global orgID

  useEffect(() => {
    if (
      templates.length > 0 &&
      availableUsers.length > 0 &&
      availableTeams.length > 0 &&
      orgData
    ) {
      handleInitializeViewers();
    }
  }, [templates, availableUsers, availableTeams, orgData]);

  const handleAddViewer = (templateId: string, viewer: Viewer) => {
    setTemplateViewers((prev) =>
      prev.map((tp) =>
        tp.templateId === templateId
          ? {
              ...tp,
              viewers: [
                ...tp.viewers.filter((p) => p.type !== 'everyone'),
                viewer,
              ],
            }
          : tp
      )
    );
  };

  const handleRemoveViewer = (templateId: string, permissionId: string) => {
    setTemplateViewers((prev) =>
      prev.map((tp) =>
        tp.templateId === templateId
          ? {
              ...tp,
              viewers: tp.viewers.filter((p) => p.id !== permissionId),
            }
          : tp
      )
    );

    // If no viewers left, add "everyone"
    const templatePerms = templateViewers.find(
      (tp) => tp.templateId === templateId
    );
    if (templatePerms && templatePerms.viewers.length === 1) {
      handleAddViewer(templateId, {
        id: `${templateId}-everyone`,
        type: 'everyone',
        name: 'Everyone',
      });
    }
  };

  const handleSaveViewers = async () => {
    setSaving(true);
    setError(null);

    try {
      // @ts-ignore
      const orgID = (global as any)?.me?.orgID;

      if (!orgID) {
        throw new Error(
          'Organization ID not found. Please refresh and try again.'
        );
      }

      if (templateViewers.length === 0) {
        throw new Error('No templates to save. Please refresh and try again.');
      }

      await saveDocuSealViewers(orgID, templateViewers);
      alert('DocuSeal viewers saved successfully!');
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Unknown error occurred';
      setError(errorMessage);
      console.error('Save error:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateViewers = (templateId: string, viewers: Viewer[]) => {
    setTemplateViewers((prev) => {
      const updated = prev.map((tp) => {
        if (tp.templateId === templateId) {
          return {
            ...tp,
            viewers,
          };
        }
        return tp;
      });

      // If template doesn't exist, create it
      const templateExists = updated.find((tp) => tp.templateId === templateId);
      if (!templateExists) {
        const templateName =
          templates.find((t) => t.id === templateId)?.name || '';
        updated.push({
          templateId,
          templateName,
          viewers,
          crms: [], // Default to all CRMs
        });
      }

      return updated;
    });
  };

  const handleUpdateCRMs = (templateId: string, crmIds: string[]) => {
    setTemplateViewers((prev) => {
      const updated = prev.map((tp) => {
        if (tp.templateId === templateId) {
          return {
            ...tp,
            crms: crmIds,
          };
        }
        return tp;
      });

      // If template doesn't exist, create it
      const templateExists = updated.find((tp) => tp.templateId === templateId);
      if (!templateExists) {
        const templateName =
          templates.find((t) => t.id === templateId)?.name || '';
        updated.push({
          templateId,
          templateName,
          viewers: [], // Default to everyone
          crms: crmIds,
        });
      }

      return updated;
    });
  };

  const handleCloseDialog = () => {
    setAddViewerDialog({ open: false, templateId: '' });
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: 300,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', height: '100%', p: 3 }}>
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h4"
          sx={(theme) => ({
            fontWeight: 600,
            mb: 1,
            color: theme.custom?.text,
          })}
        >
          DocuSeal Template Access Settings
        </Typography>
        <Typography
          variant="body1"
          sx={(theme) => ({
            mb: 2,
            color: theme.custom?.textLight,
          })}
        >
          Configure who can access each DocuSeal template. By default, templates
          are accessible to everyone.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
      </Box>

      {templates.length === 0 ? (
        <Alert severity="info">
          No DocuSeal templates found. Make sure DocuSeal templates have been
          synced to the backend.
        </Alert>
      ) : (
        <>
          {templates.map((template) => {
            const templateData = templateViewers.find(
              (tp) => tp.templateId === template.id
            );
            const viewers = templateData?.viewers || [];
            const crmIds = templateData?.crms || [];

            // Empty permissions array means everyone has access
            const hasEveryone = viewers.length === 0;

            // For display purposes, show "Everyone" when viewer array is empty
            const displayViewers = hasEveryone
              ? [
                  {
                    id: `${template.id}-everyone`,
                    type: 'everyone' as const,
                    name: 'Everyone',
                  },
                ]
              : viewers;

            // Get CRM names for display
            const displayCRMs =
              crmIds.length === 0
                ? []
                : availableCRMs.filter((crm) => crmIds.includes(crm.id));

            return (
              <CustomCard key={template.id}>
                <CardContent>
                  <Grid container spacing={3} alignItems="center">
                    <Grid item xs={12} sm={4}>
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                      >
                        <Box sx={{ fontSize: '2rem' }}>
                          <GamifyIcon icon="notes" color={theme.primary} />
                        </Box>
                        <Box>
                          <Typography
                            variant="h6"
                            sx={(theme) => ({
                              fontWeight: 600,
                              color: theme.custom?.text,
                            })}
                          >
                            {template.name}
                          </Typography>
                          {template.description && (
                            <Typography
                              variant="body2"
                              sx={(theme) => ({
                                color: theme.custom?.textLight,
                              })}
                            >
                              {template.description}
                            </Typography>
                          )}
                          {template.folder && template.folder !== 'Default' && (
                            <Typography
                              variant="caption"
                              sx={(theme) => ({
                                color: theme.custom?.textLight,
                              })}
                            >
                              Folder: {template.folder}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </Grid>

                    <Grid item xs={12} sm={8}>
                      <Box>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            mb: 2,
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={(theme) => ({
                              color: theme.custom?.textLight,
                            })}
                          >
                            Viewers:
                          </Typography>
                          <Button
                            size="small"
                            startIcon={<AddIcon />}
                            onClick={() => {
                              setAddViewerDialog({
                                open: true,
                                templateId: template.id,
                              });
                            }}
                          >
                            Manage Viewers
                          </Button>
                        </Box>

                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {displayViewers.map((viewer) => {
                            const getChipProps = () => {
                              switch (viewer.type) {
                                case 'everyone':
                                  return {
                                    color: 'primary' as const,
                                    variant: 'filled' as const,
                                  };
                                case 'user':
                                  return {
                                    color: 'info' as const,
                                    variant: 'outlined' as const,
                                  };
                                case 'team':
                                  return {
                                    color: 'success' as const,
                                    variant: 'outlined' as const,
                                  };
                                default:
                                  return {
                                    color: 'default' as const,
                                    variant: 'outlined' as const,
                                  };
                              }
                            };

                            const chipProps = getChipProps();

                            return (
                              <Chip
                                key={viewer.id}
                                label={
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 0.5,
                                    }}
                                  >
                                    {viewer.name}
                                  </Box>
                                }
                                color={chipProps.color}
                                variant={chipProps.variant}
                                size="medium"
                                onDelete={
                                  viewers.length > 1
                                    ? () =>
                                        handleRemoveViewer(
                                          template.id,
                                          viewer.id
                                        )
                                    : undefined
                                }
                                deleteIcon={<DeleteIcon />}
                                sx={{
                                  borderRadius: 2,
                                  fontWeight: 500,
                                  '& .MuiChip-label': {
                                    padding: '0 8px',
                                  },
                                  '& .MuiChip-deleteIcon': {
                                    fontSize: '18px',
                                  },
                                }}
                              />
                            );
                          })}
                        </Box>

                        {/* CRM Filtering Section */}
                        <Box sx={{ mt: 3 }}>
                          <Typography
                            variant="body2"
                            sx={(theme) => ({
                              color: theme.custom?.textLight,
                            })}
                          >
                            CRMs:{' '}
                            {displayCRMs.length === 0 ? (
                              <Typography
                                component="span"
                                variant="body2"
                                sx={(theme) => ({
                                  fontWeight: 500,
                                  color: theme.custom?.text,
                                })}
                              >
                                All CRMs
                              </Typography>
                            ) : (
                              ''
                            )}
                          </Typography>
                          {displayCRMs.length > 0 && (
                            <Box
                              sx={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: 1,
                                mt: 1,
                              }}
                            >
                              {displayCRMs.map((crm) => (
                                <Chip
                                  key={crm.id}
                                  label={crm.title}
                                  color="warning"
                                  variant="outlined"
                                  size="small"
                                  sx={{
                                    borderRadius: 2,
                                    fontWeight: 500,
                                  }}
                                />
                              ))}
                            </Box>
                          )}
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </CustomCard>
            );
          })}

          <Divider sx={{ my: 3 }} />

          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              onClick={handleSaveViewers}
              disabled={saving}
              size="large"
            >
              {saving ? <CircularProgress size={20} /> : 'Save Template Access'}
            </Button>
          </Box>
        </>
      )}

      <AddViewerDialog
        open={addViewerDialog.open}
        templateId={addViewerDialog.templateId}
        onClose={handleCloseDialog}
        availableUsers={availableUsers}
        availableTeams={availableTeams}
        availableCRMs={availableCRMs}
        templates={templates}
        templateViewers={templateViewers}
        onUpdateViewers={handleUpdateViewers}
        onUpdateCRMs={handleUpdateCRMs}
      />
    </Box>
  );
};

export default DocuSealVisibility;
